import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SettingsService,
  SettingName,
  type SettingProps,
} from 'src/api/generated';
import type { PropertyCodeDisplayPreference } from '../types/property-code-setting.type';
import { useSettings } from 'src/hooks/api/settings';

/** Read/save the tenant preference and refresh property-bearing view caches. */
export function usePropertyCodeSetting(enabled = true) {
  const queryClient = useQueryClient();
  const current = useSettings(enabled);
  const stored = current.settings.find(
    ({ name }) => name === SettingName.PROPERTY_CODE_DISPLAY
  );
  const setting: PropertyCodeDisplayPreference =
    stored?.value === 'external' ? 'external' : 'comos';
  const mutation = useMutation({
    mutationFn: (value: PropertyCodeDisplayPreference) =>
      SettingsService.updateSetting(
        SettingName.PROPERTY_CODE_DISPLAY,
        { value },
        'tenant'
      ),
    onSuccess: (saved) => {
      queryClient.setQueryData<SettingProps[]>(['settings'], (previous) => [
        ...(previous ?? []).filter((item) => item.name !== saved.name),
        saved,
      ]);
      const prefixes = [
        ['settings'],
        ['catalog/properties'],
        ['integrations', 'portal-publications'],
        ['reports', 'widget-query'],
        ['automations/employees/executions'],
        ['automations/employees/executions/steps'],
        ['websites', 'current', 'analytics'],
      ];
      for (const queryKey of prefixes) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
  return {
    ...current,
    setting,
    hasInvalidValue: stored?.hasInvalidValue === true,
    mutation,
  };
}
